import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { MoneyInput } from "@/components/MoneyInput";
import { Plus, Pencil, Trash2, ListPlus, Search, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { usePolicyTypes } from "@/lib/usePolicyTypes";

const emptyForm = {
  name: "",
  default_premium: "",
  description: "",
  policy_type: "",
  sort_order: 100,
  is_active: true,
};

export default function OfferLineItems() {
  const { options: POLICY_TYPES } = usePolicyTypes();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = new URLSearchParams(location.search).get("return_to") || "";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/offer-line-items/all");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) =>
      (it.name || "").toLowerCase().includes(q) ||
      (it.description || "").toLowerCase().includes(q) ||
      (it.policy_type || "").toLowerCase().includes(q),
    );
  }, [items, search]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (it) => {
    setEditing(it);
    setForm({
      name: it.name || "",
      default_premium: it.default_premium != null ? String(it.default_premium) : "",
      description: it.description || "",
      policy_type: it.policy_type || "",
      sort_order: it.sort_order ?? 100,
      is_active: it.is_active ?? true,
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name?.trim()) {
      toast.error("Il nome è obbligatorio");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        default_premium: form.default_premium === "" ? null : Number(form.default_premium),
        description: form.description || "",
        policy_type: form.policy_type || "",
        sort_order: Number(form.sort_order) || 100,
        is_active: !!form.is_active,
      };
      if (editing) {
        await api.put(`/offer-line-items/${editing.id}`, payload);
        toast.success("Voce aggiornata");
      } else {
        await api.post("/offer-line-items", payload);
        toast.success("Voce creata");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/offer-line-items/${deleteTarget.id}`);
      toast.success("Voce eliminata");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const typeLabel = (code) => POLICY_TYPES.find((t) => t.v === code)?.l || code || "—";

  return (
    <div data-testid="offer-line-items-page" className="space-y-6">
      <header className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          {returnTo && (
            <button
              type="button"
              data-testid="offer-items-back"
              onClick={() => navigate(returnTo, { replace: true })}
              className="text-[11px] text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1 mb-1"
              title="Torna alla pagina precedente"
            >
              <ArrowLeft className="h-3 w-3" /> Torna indietro
            </button>
          )}
          <p className="text-xs uppercase tracking-wider text-slate-500">Gestione Agenzia</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-2">
            <ListPlus className="h-7 w-7 text-indigo-600" /> Voci offerta
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Gestisci le voci ricorrenti (es. spese di gestione, diritti d&apos;agenzia, sconti) da inserire rapidamente nelle offerte finali con un click.
          </p>
        </div>
        <Button data-testid="new-line-item-btn" onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-700 text-white">
          <Plus className="h-4 w-4 mr-2" /> Nuova voce
        </Button>
      </header>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            data-testid="line-items-search"
            placeholder="Cerca per nome, descrizione, tipo polizza…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <span className="text-xs text-slate-500">{filtered.length} {filtered.length === 1 ? "voce" : "voci"}</span>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="line-items-table">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
            <tr>
              <th className="px-4 py-3 text-left">Nome</th>
              <th className="px-4 py-3 text-left">Tipo polizza</th>
              <th className="px-4 py-3 text-right">Premio default</th>
              <th className="px-4 py-3 text-left">Descrizione</th>
              <th className="px-4 py-3 text-center">Stato</th>
              <th className="px-4 py-3 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Nessuna voce. Crea la prima.</td></tr>
            ) : filtered.map((it) => (
              <tr key={it.id} data-testid={`line-item-row-${it.id}`} className="border-t border-slate-100 hover:bg-slate-50/50">
                <td className="px-4 py-3 font-medium">{it.name}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex px-2 py-0.5 text-xs rounded border ${it.policy_type ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-slate-50 border-slate-200 text-slate-500"}`}>
                    {typeLabel(it.policy_type)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right font-mono tabular-nums">
                  {it.default_premium != null ? formatCurrency(it.default_premium) : "—"}
                </td>
                <td className="px-4 py-3 text-xs text-slate-500 max-w-xs truncate">{it.description || "—"}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex px-2 py-0.5 text-[11px] rounded border ${it.is_active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-300"}`}>
                    {it.is_active ? "Attiva" : "Disattiva"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1">
                    <button data-testid={`edit-line-item-${it.id}`} onClick={() => openEdit(it)} className="p-1.5 rounded hover:bg-slate-100" title="Modifica">
                      <Pencil className="h-4 w-4 text-slate-600" />
                    </button>
                    <button data-testid={`delete-line-item-${it.id}`} onClick={() => setDeleteTarget(it)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600" title="Elimina">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[95vw] max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListPlus className="h-5 w-5 text-indigo-600" /> {editing ? "Modifica voce" : "Nuova voce offerta"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome <span className="text-red-500">*</span></Label>
              <Input
                data-testid="line-item-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="es. Spese di gestione ed emissione"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Premio default (€)</Label>
                <MoneyInput
                  data-testid="line-item-premium"
                  value={form.default_premium}
                  onChange={(v) => setForm({ ...form, default_premium: v })}
                  placeholder="es. 15,00"
                />
                <p className="text-[11px] text-slate-500 mt-1">Lascia vuoto per inserire l&apos;importo manualmente ogni volta.</p>
              </div>
              <div>
                <Label>Ordine</Label>
                <Input
                  type="number"
                  data-testid="line-item-sort"
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Tipo polizza</Label>
              <Select value={form.policy_type || "__all__"} onValueChange={(v) => setForm({ ...form, policy_type: v === "__all__" ? "" : v })}>
                <SelectTrigger data-testid="line-item-policy-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Tutte le polizze</SelectItem>
                  {POLICY_TYPES.map((t) => (
                    <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">Limita la voce a un tipo specifico o lascia &quot;Tutte&quot; per renderla universale.</p>
            </div>
            <div>
              <Label>Descrizione</Label>
              <Textarea
                data-testid="line-item-description"
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Nota interna o testo che apparirà nella riga di copertura"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="line-item-active"
                checked={!!form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                className="h-4 w-4 accent-indigo-600"
              />
              <span className="text-sm">Voce attiva (visibile nel selettore offerte)</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Annulla</Button>
            <Button data-testid="save-line-item-btn" onClick={submit} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white">
              {busy ? "Salvataggio…" : (editing ? "Salva modifiche" : "Crea voce")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent className="w-[95vw] max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Trash2 className="h-5 w-5 text-rose-600" /> Elimina voce</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Confermi l&apos;eliminazione della voce <strong>{deleteTarget?.name}</strong>? Le offerte già create non verranno modificate.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Annulla</Button>
            <Button data-testid="confirm-delete-line-item" onClick={doDelete} className="bg-rose-600 hover:bg-rose-700 text-white">
              Elimina definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
